import { useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useAppStore } from '@core/store'
import type { ClashResult } from '@viewer/clash/lib'
import { useNomesDosModelos } from '@viewer/lib/useNomeDoModelo'
import {
  AGRUPAMENTO_POR_OMISSAO,
  NOME_DO_METODO,
  topicosPorPrioridade,
  type ClashAgrupamentoConfig,
  type MetodoBase,
  type MetodoDeAgrupamento,
} from './clashAgrupamento'
import {
  REGRAS,
  prioridadeDaRegra,
  REGRAS_POR_OMISSAO,
  textoDaHierarquia,
  type ClashRegrasConfig,
} from './clashClassificacao'
import { calcularGrupos, itensClassificados, lerEClassificar } from './clashClassificar'
import {
  deduzirEspecialidade,
  ESPECIALIDADES_DA_CLASSIFICACAO,
  NOME_DA_ESPECIALIDADE,
  isEspecialidade,
  type Especialidade,
} from './clashEspecialidades'
import { clashKey, PRIORITIES, PRIORITY_LABELS, type ClashPriority } from './clashTriage'

// ---------------------------------------------------------------------------
// Painel "Classificação e agrupamento": a especialidade de cada modelo,
// as regras de classificação (tabela, limiares e hierarquia editáveis),
// "Classificar automaticamente", o método de agrupamento com "Agrupar", e
// as edições à mão dos grupos (juntar, tirar, renomear).
// ---------------------------------------------------------------------------

const CAMPO =
  'rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[11px]'
const BOTAO =
  'rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[11px] disabled:opacity-40'

const METODOS: MetodoDeAgrupamento[] = ['misto', 'sitio', 'elemento', 'familia', 'nivel']
const METODOS_BASE: MetodoBase[] = ['sitio', 'elemento', 'familia', 'nivel']

/** Limiares editáveis, com rótulo e unidade. */
const LIMIARES: { k: keyof ClashRegrasConfig; rotulo: string; unidade: string; passo?: number }[] = [
  { k: 'diametroVigaPilarMm', rotulo: 'Ø crítico em vigas e pilares', unidade: 'mm' },
  { k: 'diametroLajeMm', rotulo: 'Ø crítico em lajes', unidade: 'mm' },
  { k: 'profundidadeMm', rotulo: 'Profundidade que conta', unidade: 'mm' },
  { k: 'folgaCriticaMm', rotulo: 'Folga abaixo da qual é Média', unidade: 'mm' },
  { k: 'folgaAlvoMm', rotulo: 'Folga pedida entre redes', unidade: 'mm' },
  { k: 'diametroEelPequenoMm', rotulo: 'Ø de tubo eléctrico de furação corrente', unidade: 'mm' },
  { k: 'fraccaoDuplicacao', rotulo: 'Fracção EST dentro do ARQ (duplicação)', unidade: '0 a 1', passo: 0.05 },
  { k: 'fraccaoEmbebido', rotulo: 'Fracção da rede dentro do ARQ (embebido)', unidade: '0 a 1', passo: 0.05 },
  { k: 'paredeEspessaMm', rotulo: 'Parede a confirmar se é resistente', unidade: 'mm' },
]

export interface ClashClassificacaoPainelProps {
  results: readonly ClashResult[]
  checked: ReadonlySet<string>
  focusKey: string | null
}

export default function ClashClassificacaoPainel({ results, checked, focusKey }: ClashClassificacaoPainelProps) {
  const models = useAppStore((s) => s.models)
  const estado = useAppStore((s) => s.clashClassificacao)
  const triagem = useAppStore((s) => s.clashTriage)
  const setEstado = useAppStore((s) => s.setClashClassificacao)
  const classificar = useAppStore((s) => s.classificarClashTriagem)
  const agruparNaTriagem = useAppStore((s) => s.agruparClashTriagem)
  const juntar = useAppStore((s) => s.juntarClashGrupos)
  const tirar = useAppStore((s) => s.tirarDoClashGrupo)
  const renomear = useAppStore((s) => s.renomearClashGrupo)
  const setGroupBy = useAppStore((s) => s.setClashGroupBy)
  const { nomeDe } = useNomesDosModelos()

  const [aberto, setAberto] = useState(false)
  const [regrasAbertas, setRegrasAbertas] = useState(false)
  const [ocupado, setOcupado] = useState<string | null>(null)
  const [mensagem, setMensagem] = useState<string | null>(null)
  const [novoTitulo, setNovoTitulo] = useState('')
  const sinal = useRef<{ aborted: boolean } | null>(null)

  const regras = estado.regras
  const agr = estado.agrupamento
  const setRegras = (patch: Partial<ClashRegrasConfig>) => setEstado({ regras: { ...regras, ...patch } })
  const setAgr = (patch: Partial<ClashAgrupamentoConfig>) => setEstado({ agrupamento: { ...agr, ...patch } })

  const gruposMarcados = useMemo(() => {
    const ids = new Set<string>()
    for (const k of checked) {
      const g = triagem[k]?.group
      if (g) ids.add(g)
    }
    return [...ids]
  }, [checked, triagem])
  const marcadosComGrupo = [...checked].filter((k) => triagem[k]?.group)
  // Os grupos dos conflitos desta corrida. O estado guarda também os de
  // conflitos que já não aparecem (resolvidos ou por verificar), e o
  // cabeçalho dizia 112 com a lista a mostrar 102.
  const gruposNaCorrida = useMemo(() => {
    const ids = new Set<string>()
    for (const r of results) {
      const g = triagem[clashKey(r)]?.group
      if (g) ids.add(g)
    }
    return ids.size
  }, [results, triagem])
  const grupoEmFoco = focusKey ? (triagem[focusKey]?.group ?? null) : null

  const ler = async () => {
    const s = { aborted: false }
    sinal.current = s
    return lerEClassificar(results, useAppStore.getState().clashClassificacao, {
      signal: s,
      onProgress: (p) =>
        setOcupado(
          p.fase === 'propriedades'
            ? `A ler propriedades ${p.feitos}/${p.total}`
            : p.fase === 'geometria'
              ? 'A ler a geometria'
              : `A medir volumes comuns ${p.feitos}/${p.total}`,
        ),
    })
  }

  const onClassificar = async () => {
    setOcupado('A ler os modelos')
    setMensagem(null)
    try {
      const leitura = await ler()
      const r = classificar(itensClassificados(results, leitura))
      setMensagem(
        `${r.aplicadas} conflito${r.aplicadas === 1 ? '' : 's'} classificado${r.aplicadas === 1 ? '' : 's'} pelas regras; `
          + `${r.mantidas} com parecer manual ou de proposta mantido${r.mantidas === 1 ? '' : 's'}; `
          + `${r.foraDoAmbito} fora do âmbito.`,
      )
    } catch (err) {
      setMensagem(err instanceof Error ? err.message : 'Não foi possível classificar.')
    } finally {
      setOcupado(null)
      sinal.current = null
    }
  }

  const onAgrupar = async () => {
    setOcupado('A ler os modelos')
    setMensagem(null)
    try {
      const leitura = await ler()
      const st = useAppStore.getState()
      const grupos = calcularGrupos(results, leitura, st.clashTriage, st.clashClassificacao)
      agruparNaTriagem(grupos, new Set(results.map(clashKey)))
      setGroupBy('grupos')
      const porPrio = topicosPorPrioridade(grupos)
      setMensagem(
        `${grupos.length} grupo${grupos.length === 1 ? '' : 's'} ${NOME_DO_METODO[agr.metodo]}: `
          + Object.entries(porPrio)
            .map(([p, n]) => `${p} ${n}`)
            .join(', ')
          + '. Os grupos editados à mão ficaram como estavam.',
      )
    } catch (err) {
      setMensagem(err instanceof Error ? err.message : 'Não foi possível agrupar.')
    } finally {
      setOcupado(null)
      sinal.current = null
    }
  }

  const hierarquia = regras.hierarquia
  const trocarHierarquia = (pos: number, esp: Especialidade) => {
    const outra = hierarquia.indexOf(esp)
    const h = [...hierarquia]
    h[outra] = h[pos]
    h[pos] = esp
    setRegras({ hierarquia: h })
  }

  return (
    <div className="border-b border-[var(--color-border)] text-[11px]" data-testid="clash-classificacao">
      <button
        type="button"
        onClick={() => setAberto(!aberto)}
        className="flex w-full items-center gap-1 px-3 py-1.5 text-left text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        aria-expanded={aberto}
      >
        {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Classificação e agrupamento
        {estado.agrupadoEm && (
          <span className="ml-auto text-[10px] text-[var(--color-text-dim)]">
            {gruposNaCorrida} grupos, {NOME_DO_METODO[agr.metodo]}
          </span>
        )}
      </button>
      {aberto && (
        <div className="flex flex-col gap-2 px-3 pb-2">
          {/* Especialidades */}
          <div className="flex flex-col gap-0.5">
            <span className="text-[var(--color-text-dim)]">Especialidade de cada modelo</span>
            {models.map((m) => {
              const corrigido = m.id in estado.especialidades
              const valor = corrigido ? estado.especialidades[m.id] : deduzirEspecialidade(m.fileName)
              return (
                <label key={m.id} className="flex items-center gap-1">
                  <span className="min-w-0 flex-1 truncate" title={nomeDe(m.id)?.completo ?? m.fileName}>
                    {nomeDe(m.id)?.curto ?? m.fileName}
                  </span>
                  <select
                    value={valor ?? ''}
                    onChange={(e) => {
                      const v = e.target.value
                      setEstado({
                        especialidades: { ...estado.especialidades, [m.id]: isEspecialidade(v) ? v : null },
                      })
                    }}
                    aria-label={`Especialidade de ${m.fileName}`}
                    className={CAMPO}
                  >
                    <option value="">sem especialidade</option>
                    {ESPECIALIDADES_DA_CLASSIFICACAO.map((e) => (
                      <option key={e} value={e}>
                        {e} ({NOME_DA_ESPECIALIDADE[e]})
                      </option>
                    ))}
                  </select>
                  {!corrigido && <span className="text-[10px] text-[var(--color-text-dim)]">deduzida</span>}
                </label>
              )
            })}
          </div>

          {/* Regras */}
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setRegrasAbertas(!regrasAbertas)}
              className="flex items-center gap-1 text-left text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              aria-expanded={regrasAbertas}
            >
              {regrasAbertas ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Regras de classificação
            </button>
            {regrasAbertas && (
              <div className="flex flex-col gap-1">
                <table className="w-full border-collapse text-[10px]">
                  <thead>
                    <tr className="text-left text-[var(--color-text-dim)]">
                      <th className="pr-1">Regra</th>
                      <th className="pr-1">Prioridade</th>
                      <th>Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {REGRAS.map((r) => (
                      <tr key={r.regra} className="align-top">
                        <td className="pr-1 font-mono">{r.regra}</td>
                        <td className="pr-1">
                          {r.prioridade ? (
                            <select
                              value={prioridadeDaRegra(r.regra, regras) ?? r.prioridade}
                              onChange={(e) => {
                                const v = e.target.value as ClashPriority
                                const resto = { ...regras.prioridades }
                                // Igual à da tabela: deixa de ser uma escolha.
                                if (v === r.prioridade) delete resto[r.regra]
                                else resto[r.regra] = v
                                setRegras({ prioridades: resto })
                              }}
                              aria-label={`Prioridade da regra ${r.regra}`}
                              title={
                                prioridadeDaRegra(r.regra, regras) !== r.prioridade
                                  ? `Escolhida (a da tabela é ${PRIORITY_LABELS[r.prioridade]}). Aplica-se aos conflitos sem parecer manual em "Classificar".`
                                  : 'A prioridade desta regra. Muda-a para todos os conflitos que a regra apanha (os pareceres manuais ficam).'
                              }
                              className={`${CAMPO} ${prioridadeDaRegra(r.regra, regras) !== r.prioridade ? 'border-[var(--color-accent)]' : ''}`}
                            >
                              {(['critica', 'alta', 'media', 'baixa'] as ClashPriority[]).map((p) => (
                                <option key={p} value={p}>
                                  {PRIORITY_LABELS[p]}
                                </option>
                              ))}
                            </select>
                          ) : (
                            'fora do âmbito'
                          )}
                        </td>
                        <td>{r.descricao(regras)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="grid grid-cols-1 gap-1 @md:grid-cols-2">
                  {LIMIARES.map((l) => (
                    <label key={l.k} className="flex items-center gap-1">
                      <span className="min-w-0 flex-1 text-[var(--color-text-dim)]">{l.rotulo}</span>
                      <input
                        type="number"
                        min={0}
                        step={l.passo ?? 1}
                        value={regras[l.k] as number}
                        onChange={(e) => {
                          const v = Number(e.target.value)
                          if (Number.isFinite(v) && v >= 0) setRegras({ [l.k]: v } as Partial<ClashRegrasConfig>)
                        }}
                        className={`${CAMPO} w-16`}
                        aria-label={l.rotulo}
                      />
                      <span className="w-10 text-[10px] text-[var(--color-text-dim)]">{l.unidade}</span>
                    </label>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-[var(--color-text-dim)]">Hierarquia entre redes:</span>
                  {[0, 1, 2].map((pos) => (
                    <select
                      key={pos}
                      value={hierarquia[pos]}
                      onChange={(e) => trocarHierarquia(pos, e.target.value as Especialidade)}
                      aria-label={`Posição ${pos + 1} da hierarquia`}
                      className={CAMPO}
                    >
                      {(['HID', 'MAC', 'EEL'] as Especialidade[]).map((e) => (
                        <option key={e} value={e}>
                          {e}
                        </option>
                      ))}
                    </select>
                  ))}
                  <span className="text-[10px] text-[var(--color-text-dim)]">{textoDaHierarquia(hierarquia)}</span>
                </div>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={regras.folgasSoEntreRedes}
                    onChange={(e) => setRegras({ folgasSoEntreRedes: e.target.checked })}
                    className="accent-[var(--color-accent)]"
                  />
                  Folgas só entre redes (as folgas com EST ou ARQ ficam fora do âmbito)
                </label>
                <div>
                  <button
                    type="button"
                    onClick={() => setEstado({ regras: { ...REGRAS_POR_OMISSAO, hierarquia: [...REGRAS_POR_OMISSAO.hierarquia] } })}
                    className={BOTAO}
                  >
                    Repor os valores por omissão
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => void onClassificar()}
              disabled={!!ocupado || results.length === 0}
              className={BOTAO}
              title="Aplica as regras aos conflitos sem parecer, ou com parecer dado por uma regra. Um parecer manual ou de uma proposta importada nunca é alterado."
            >
              Classificar automaticamente
            </button>
          </div>

          {/* Agrupamento */}
          <div className="flex flex-col gap-1">
            <span className="text-[var(--color-text-dim)]">Agrupamento em tópicos</span>
            <div className="flex flex-wrap items-center gap-1">
              <select
                value={agr.metodo}
                onChange={(e) => setAgr({ metodo: e.target.value as MetodoDeAgrupamento })}
                aria-label="Método de agrupamento"
                className={CAMPO}
              >
                {METODOS.map((m) => (
                  <option key={m} value={m}>
                    {NOME_DO_METODO[m]}
                  </option>
                ))}
              </select>
              {(agr.metodo === 'sitio' || agr.metodo === 'misto') && (
                <label className="flex items-center gap-1">
                  Raio
                  <input
                    type="number"
                    min={0.1}
                    step={0.1}
                    value={agr.raioM}
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      if (v > 0) setAgr({ raioM: v })
                    }}
                    className={`${CAMPO} w-14`}
                    aria-label="Raio do sítio físico, em metros"
                  />
                  m
                </label>
              )}
              {(agr.metodo === 'familia' || agr.metodo === 'misto') && (
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={agr.familiaComNivel}
                    onChange={(e) => setAgr({ familiaComNivel: e.target.checked })}
                    className="accent-[var(--color-accent)]"
                  />
                  família por nível
                </label>
              )}
              <label className="flex items-center gap-1" title="Acima disto, o grupo corta-se pela mediana em planta">
                Máx.
                <input
                  type="number"
                  min={2}
                  value={agr.maxPares}
                  onChange={(e) => {
                    const v = Math.floor(Number(e.target.value))
                    if (v >= 2) setAgr({ maxPares: v })
                  }}
                  className={`${CAMPO} w-12`}
                  aria-label="Máximo de conflitos por grupo"
                />
              </label>
            </div>
            {agr.metodo === 'misto' && (
              <div className="flex flex-wrap items-center gap-1">
                {PRIORITIES.map((p) => (
                  <label key={p} className="flex items-center gap-1">
                    {PRIORITY_LABELS[p]}
                    <select
                      value={agr.misto[p]}
                      onChange={(e) => setAgr({ misto: { ...agr.misto, [p]: e.target.value as MetodoBase } })}
                      aria-label={`Método para a prioridade ${PRIORITY_LABELS[p]}`}
                      className={CAMPO}
                    >
                      {METODOS_BASE.map((m) => (
                        <option key={m} value={m}>
                          {NOME_DO_METODO[m]}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
                <button
                  type="button"
                  onClick={() => setAgr({ ...AGRUPAMENTO_POR_OMISSAO, misto: { ...AGRUPAMENTO_POR_OMISSAO.misto } })}
                  className={BOTAO}
                >
                  Repor
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1">
              <button
                type="button"
                onClick={() => void onAgrupar()}
                disabled={!!ocupado || results.length === 0}
                className={BOTAO}
                title="Agrupa os conflitos desta corrida com o método escolhido. Substitui os grupos automáticos e os da proposta; os grupos editados à mão ficam como estão."
              >
                Agrupar
              </button>
            </div>
          </div>

          {/* Edicao a mao */}
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => juntar(gruposMarcados)}
              disabled={gruposMarcados.length < 2}
              className={BOTAO}
              title="Junta num só os grupos dos conflitos marcados (fica o primeiro, com o seu Guid)"
            >
              Juntar os grupos dos marcados
            </button>
            <button
              type="button"
              onClick={() => tirar(marcadosComGrupo)}
              disabled={marcadosComGrupo.length === 0}
              className={BOTAO}
              title="Tira os conflitos marcados do seu grupo; voltar a agrupar não os volta a pôr"
            >
              Tirar os marcados do grupo
            </button>
            {grupoEmFoco && (
              <span className="flex items-center gap-1">
                <input
                  value={novoTitulo}
                  onChange={(e) => setNovoTitulo(e.target.value)}
                  placeholder={triagem[focusKey!]?.groupTitle ?? 'Novo título do grupo'}
                  aria-label="Novo título do grupo do conflito em foco"
                  className={`${CAMPO} w-56`}
                />
                <button
                  type="button"
                  onClick={() => {
                    renomear(grupoEmFoco, novoTitulo)
                    setNovoTitulo('')
                  }}
                  disabled={!novoTitulo.trim()}
                  className={BOTAO}
                >
                  Renomear o grupo
                </button>
              </span>
            )}
          </div>

          {ocupado && (
            <div className="flex items-center gap-2" role="status">
              <span className="flex-1 text-[var(--color-text-muted)]">{ocupado}</span>
              <button
                type="button"
                onClick={() => {
                  if (sinal.current) sinal.current.aborted = true
                }}
                className={BOTAO}
              >
                Cancelar
              </button>
            </div>
          )}
          {mensagem && (
            <p className="leading-snug text-[var(--color-text-muted)]" role="status">
              {mensagem}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
